import { useNavigate } from '@tanstack/react-router';
import { Eye } from 'lucide-react';
import { type ComponentProps, memo, useState } from 'react';
import { CircleFlagLanguage } from 'react-circle-flags';
import { useEventCallback } from 'usehooks-ts';

import { DuoFeedbackModal } from '@/features/duos/modals/duoFeedbackModal';
import { ReportProfileActions } from '@/features/profile/components/reportProfileActions';
import { useGetProfile } from '@/features/profile/hooks/useGetProfile';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/utils/cn';

export interface DuoActivityActionsProps extends ComponentProps<'div'> {
  partnerUserId: string;
  canReview: boolean;
}

export const DuoActivityActions = memo(function DuoActivityActions(
  props: DuoActivityActionsProps,
) {
  const { className, partnerUserId, canReview, ...rest } = props;
  const navigate = useNavigate();

  const { data: languageCodes } = useGetProfile({
    userId: partnerUserId,
    options: {
      select: data => data.profile?.languages?.write,
    },
  });

  const handleViewProfile = useEventCallback(() => {
    void navigate({
      to: '/profiles/$profileId',
      params: { profileId: partnerUserId },
    });
  });

  const handleOpenComms = useEventCallback(() => {
    void navigate({
      to: '/duo-chats/$duoId',
      params: { duoId: partnerUserId },
    });
  });

  return (
    <div
      {...rest}
      className={cn('flex items-center gap-1 px-2 pt-4 pb-2', className)}
    >
      {canReview && <DuoReviewButton userId={partnerUserId} />}
      <Button variant="outline" className="flex-1" onClick={handleOpenComms}>
        {languageCodes?.[0] && (
          <CircleFlagLanguage
            className="size-4"
            languageCode={languageCodes[0]}
          />
        )}{' '}
        Open comms
      </Button>
      <Button variant="outline" size="icon" onClick={handleViewProfile}>
        <Eye className="size-4" />
      </Button>
      <ReportProfileActions userId={partnerUserId} isDuo />
    </div>
  );
});

function DuoReviewButton({ userId }: { userId: string }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleReviewClick = () => {
    setIsModalOpen(true);
  };

  return (
    <>
      <Button variant="default" className="flex-1" onClick={handleReviewClick}>
        Review
      </Button>
      <DuoFeedbackModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        userId={userId}
      />
    </>
  );
}
